import React, { Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { TourProvider } from "./context/TourContext";
import { Toaster } from "./components/ui/sonner";
import useAnalyticsPageView from "./hooks/useAnalyticsPageView";
import lazyWithRetry from "./utils/lazyWithRetry";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import DemoModeBanner from "./components/DemoModeBanner";

// ─── EAGERLY LOADED (the hot public entry points) ─────────────────────
// HomePage is the LCP-critical route — every cold visitor lands here.
// Login/Register are common enough that lazy-loading them adds a jarring
// spinner. ProbateChecklistPage is our lead-magnet landing page and is
// linked from ad campaigns / emails.
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ProbateChecklistPage from "./pages/ProbateChecklistPage";
// AuthCallback must be eager — it renders the instant the Google OAuth
// redirect lands with #session_id= in the URL.
import AuthCallback from "./pages/AuthCallback";

// TrialExpiredPaywall is rendered globally as a portal-style overlay so it
// must be eager (won't be picked up by a route-level Suspense boundary).
import TrialExpiredPaywall from "./components/TrialExpiredPaywall";
import DeployUpdateBanner from "./components/DeployUpdateBanner";

// ─── LAZY LOADED (everything else — split into its own bundle) ─────────
// This is the LCP win: initial download drops from ~30 page chunks to
// just the four eager pages + shell, so the landing hero can paint in
// ~2-3s on cold visits instead of 11s.
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const CreateEstatePage = lazyWithRetry(() => import("./pages/CreateEstatePage"));
const EstateDetailPage = lazyWithRetry(() => import("./pages/EstateDetailPage"));
const InvitationPage = lazyWithRetry(() => import("./pages/InvitationPage"));
const FeaturesPage = lazyWithRetry(() => import("./pages/FeaturesPage"));
const PricingPage = lazyWithRetry(() => import("./pages/PricingPage"));
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage"));
const ContactPage = lazyWithRetry(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazyWithRetry(() => import("./pages/TermsOfServicePage"));
const BrochurePage = lazyWithRetry(() => import("./pages/BrochurePage"));
const DemoPage = lazyWithRetry(() => import("./pages/DemoPage"));
const VideoRecordingPage = lazyWithRetry(() => import("./pages/VideoRecordingPage"));
const ShareStoryPage = lazyWithRetry(() => import("./pages/ShareStoryPage"));
const AdminTestimonialsPage = lazyWithRetry(() => import("./pages/AdminTestimonialsPage"));
const SubscriptionSuccessPage = lazyWithRetry(() => import("./pages/SubscriptionSuccessPage"));
const FeatureTourPage = lazyWithRetry(() => import("./pages/FeatureTourPage"));
const HelpPage = lazyWithRetry(() => import("./pages/HelpPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const FirmBrandingPage = lazyWithRetry(() => import("./pages/FirmBrandingPage"));
const PublicEstateViewPage = lazyWithRetry(() => import("./pages/PublicEstateViewPage"));
const AttorneyUnsubscribePage = lazyWithRetry(() => import("./pages/AttorneyUnsubscribePage"));
const ExecutorKitDocPage = lazyWithRetry(() => import("./pages/ExecutorKitDocPage"));
const SecurityPage = lazyWithRetry(() => import("./pages/SecurityPage"));
const SecuritySettingsPage = lazyWithRetry(() => import("./pages/SecuritySettingsPage"));

// ProductTour is a heavy driver.js overlay — only load it once the shell
// has painted so it doesn't sit on the LCP critical path.
const ProductTour = lazyWithRetry(() =>
  import("./components/ProductTour").then((m) => ({ default: m.ProductTour }))
);

// Full-screen spinner used both by protected routes (waiting for auth) and
// by the Suspense boundary while a lazy chunk downloads.
const FullPageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-4 border-[#3498db] border-t-transparent rounded-full"></div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Auth Route - redirects to dashboard if already logged in
const AuthRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  useAnalyticsPageView();
  // Google OAuth return: must be read from useLocation() (reactive), checked
  // synchronously during render so the callback wins over ProtectedRoute.
  const location = useLocation();

  useEffect(() => {
    // Removes the Emergent-injected badge. We rely on a MutationObserver
    // (event-driven) instead of a 300ms setInterval — that timer was
    // measurable in the INP budget and reported by Clarity as a source of
    // main-thread pressure.
    const removeBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    // The chunk-reload guard (`lazyWithRetry`) sets this flag before doing
    // a single force-reload to recover from a stale-HTML deploy. Once we've
    // successfully mounted the shell, the guard has done its job for this
    // session — clear it so a SECOND deploy later in the same tab can also
    // benefit from the auto-reload rather than dropping to the error
    // boundary. We wait 4s so any post-mount lazy import that would fail
    // has time to surface first.
    const clearId = setTimeout(() => {
      try { sessionStorage.removeItem('epf.chunk_reload_attempted'); } catch {}
    }, 4000);

    return () => {
      observer.disconnect();
      clearTimeout(clearId);
    };
  }, []);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/brochure" element={<BrochurePage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/video-recording" element={<VideoRecordingPage />} />
        <Route path="/share-story" element={<ShareStoryPage />} />
        <Route path="/tour" element={<FeatureTourPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/attorney-unsubscribe" element={<AttorneyUnsubscribePage />} />
        <Route path="/executor-kit/:slug" element={<ExecutorKitDocPage />} />
        <Route
          path="/admin/testimonials"
          element={<ProtectedRoute><AdminTestimonialsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin"
          element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>}
        />
        <Route
          path="/firm-branding"
          element={<ProtectedRoute><FirmBrandingPage /></ProtectedRoute>}
        />
        <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
        <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/probate-checklist" element={<ProbateChecklistPage />} />
        <Route path="/public/estate/:token" element={<PublicEstateViewPage />} />
        <Route
          path="/dashboard"
          element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
        />
        <Route
          path="/estates/new"
          element={<ProtectedRoute><CreateEstatePage /></ProtectedRoute>}
        />
        <Route
          path="/estates/:id"
          element={<ProtectedRoute><EstateDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/settings/security"
          element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>}
        />
        <Route path="/invite/:token" element={<InvitationPage />} />
        <Route
          path="/subscription/success"
          element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>}
        />
      </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <TourProvider>
              <DemoModeBanner />
              <AppRoutes />
              <Suspense fallback={null}>
                <ProductTour />
              </Suspense>
              <TrialExpiredPaywall />
              <DeployUpdateBanner />
              <Toaster />
            </TourProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
